import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import { COLLAR_COLORS, collarFor } from "@/lib/collarColors";
import { formatWeight, suggestPuppyEntryUnit, toGrams } from "@/lib/weight";
import { Plus, ArrowLeft, Trash2, Calendar, Heart, Activity, Send, Sparkles, Baby, TrendingUp, TrendingDown, Minus, Download } from "lucide-react";
import { toast } from "sonner";
import { parseISO, differenceInCalendarDays } from "date-fns";
import { LineChart, Line, ResponsiveContainer, Tooltip as RTooltip, YAxis } from "recharts";
import InviteOwnerDialog from "@/components/breeder/InviteOwnerDialog";

const ENS_EXERCISES = [
  { key: "tactile_qtip", label: "Tactile (Q-tip)" },
  { key: "head_erect",   label: "Head erect"   },
  { key: "head_down",    label: "Head down"    },
  { key: "supine",       label: "Supine"       },
  { key: "cold_stim",    label: "Cold stim"    },
];

const SOC_CATEGORIES = [
  { key: "surfaces",    label: "Surfaces"    },
  { key: "sounds",      label: "Sounds"      },
  { key: "people",      label: "People"      },
  { key: "locations",   label: "Locations"   },
  { key: "food_bowls",  label: "Food bowls"  },
  { key: "toys",        label: "Toys"        },
  { key: "challenges",  label: "Challenges"  },
  { key: "freeform",    label: "Other"       },
];

// Small pure helpers — extracted to keep JSX free of nested ternaries.
const SEX_LABELS = { M: "Male", F: "Female" };
const sexLabel = (s) => SEX_LABELS[s] || "—";

const ensCellStyle = (doneCount) => {
  if (doneCount === 5) return "bg-primary text-primary-foreground border-primary";
  if (doneCount > 0) return "bg-secondary text-secondary-foreground border-border";
  return "bg-card border-border text-muted-foreground hover:bg-muted";
};

export default function LitterDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const prefUnit = user?.weight_unit_preference || "lb";
  const [litter, setLitter] = useState(null);
  const [puppies, setPuppies] = useState([]);
  const [ens, setEns] = useState([]);
  const [soc, setSoc] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [l, e, s] = await Promise.all([
        api.get(`/litters/${id}`),
        api.get(`/litters/${id}/ens`),
        api.get(`/litters/${id}/socialization`),
      ]);
      setLitter(l.data);
      setPuppies(l.data.puppies || []);
      setEns(e.data.items || []);
      setSoc(s.data.items || []);
    } catch (err) {
      toast.error("Could not load litter");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const updateLitter = async (patch) => {
    try {
      const { data } = await api.put(`/litters/${id}`, { ...litter, ...patch });
      setLitter((prev) => ({ ...prev, ...data }));
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const promote = async () => {
    if (!window.confirm("Promote this planned litter to an actual litter?")) return;
    try {
      const { data } = await api.post(`/litters/${id}/promote`);
      setLitter((prev) => ({ ...prev, ...data, is_planned: false }));
      toast.success("Litter promoted — you can now log puppies, weights, and ENS");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not promote");
    }
  };

  if (loading || !litter) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-10" data-testid="litter-detail-page">
      <div className="flex items-center gap-3">
        <Link to="/breeder/litters" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="litter-back">
          <ArrowLeft size={14} /> Back to litters
        </Link>
      </div>

      <SectionTitle
        overline={litter.is_planned ? "Breeder · Planned" : "Breeder"}
        title={(litter.dam_name || "Dam") + (litter.stud_name ? ` × ${litter.stud_name}` : "")}
      />

      {litter.is_planned ? (
        <Card className="border-amber-500/40 bg-amber-500/5 shadow-none rounded-lg" data-testid="planned-litter-banner">
          <CardContent className="p-4 sm:p-5 flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-700 flex items-center gap-1.5">
                <Sparkles size={13} /> Planned litter
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                This is a sketch — perfect for pairing plans and target dates. When the breeding actually happens, promote it to unlock puppy logging, weights, ENS, and socialization.
              </p>
            </div>
            <Button
              size="sm"
              onClick={promote}
              className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
              data-testid="planned-litter-promote"
            >
              <Baby size={14} className="mr-1.5" /> Promote to actual litter
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {/* === Litter facts (stud, dam, dates, fees) === */}
      <LitterFacts litter={litter} onSave={updateLitter} />

      {/* === Gestation timeline === */}
      {litter.timeline && <GestationTimeline timeline={litter.timeline} />}

      {/* === Puppies === */}
      <Puppies litterId={id} puppies={puppies} prefUnit={prefUnit} onChanged={reload} />

      {/* === Daily weights (unified across the litter) === */}
      <LitterWeightsGrid puppies={puppies} prefUnit={prefUnit} bornOn={litter.born_on} />

      {/* === ENS === */}
      <ENSGrid litterId={id} puppies={puppies} ens={ens} bornOn={litter.born_on} onReload={reload} />

      {/* === Socialization === */}
      <Socialization litterId={id} puppies={puppies} entries={soc} onReload={reload} />
    </div>
  );
}

// ----------------------------------------------------------------
function LitterFacts({ litter, onSave }) {
  const [form, setForm] = useState(litter);
  useEffect(() => setForm(litter), [litter]);

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="litter-facts">
      <CardContent className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Dam name"><Input value={form.dam_name || ""} onChange={(e) => setForm({ ...form, dam_name: e.target.value })} data-testid="litter-edit-dam-name" /></Field>
        <Field label="Stud"><Input value={form.stud_name || ""} onChange={(e) => setForm({ ...form, stud_name: e.target.value })} data-testid="litter-edit-stud" /></Field>
        <Field label="Back-up stud"><Input value={form.backup_stud_name || ""} onChange={(e) => setForm({ ...form, backup_stud_name: e.target.value })} placeholder="Optional fallback sire" data-testid="litter-edit-backup-stud" /></Field>
        <Field label="Stud owner"><Input value={form.stud_owner || ""} onChange={(e) => setForm({ ...form, stud_owner: e.target.value })} /></Field>
        <Field label="Stud fee ($)"><Input type="number" step="0.01" value={form.stud_fee || 0} onChange={(e) => setForm({ ...form, stud_fee: parseFloat(e.target.value) || 0 })} data-testid="litter-edit-stud-fee" /></Field>
        <Field label="Breeding date"><Input type="date" value={form.bred_on || ""} onChange={(e) => setForm({ ...form, bred_on: e.target.value })} /></Field>
        <Field label="Birth date"><Input type="date" value={form.born_on || ""} onChange={(e) => setForm({ ...form, born_on: e.target.value })} data-testid="litter-edit-born-on" /></Field>
        <Field label="Birth time"><Input type="time" value={form.birth_time || ""} onChange={(e) => setForm({ ...form, birth_time: e.target.value })} data-testid="litter-edit-birth-time" /></Field>
        <div />
        <Field label="Bitch health notes" full><Textarea rows={3} value={form.bitch_health_notes || ""} onChange={(e) => setForm({ ...form, bitch_health_notes: e.target.value })} data-testid="litter-edit-bitch-health" /></Field>
        <Field label="Ease of delivery / delivery notes" full><Textarea rows={3} value={form.delivery_notes || ""} onChange={(e) => setForm({ ...form, delivery_notes: e.target.value })} data-testid="litter-edit-delivery" /></Field>
        <div className="sm:col-span-2 flex justify-end">
          <Button onClick={() => onSave(form)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="litter-facts-save">Save</Button>
        </div>
      </CardContent>
    </Card>
  );
}
function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

// ----------------------------------------------------------------
function GestationTimeline({ timeline }) {
  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="litter-timeline">
      <CardContent className="p-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
        <TLNode icon={<Heart size={14} />} label="Ultrasound" value={timeline.ultrasound_day_28_30?.join(" → ")} sub="days 28–30" />
        <TLNode icon={<Calendar size={14} />} label="X-ray" value={timeline.xray_day_45_55?.join(" → ")} sub="days 45–55" />
        <TLNode icon={<Activity size={14} />} label="Whelp window" value={timeline.whelp_window_day_58_65?.join(" → ")} sub="days 58–65" />
        <TLNode icon={<Heart size={14} className="text-primary" />} label="Expected" value={timeline.expected_whelp_day_63} sub={timeline.days_remaining >= 0 ? `in ${timeline.days_remaining}d` : `${Math.abs(timeline.days_remaining)}d ago`} highlight />
      </CardContent>
    </Card>
  );
}
function TLNode({ icon, label, value, sub, highlight }) {
  return (
    <div className={highlight ? "bg-secondary -m-2 p-3 rounded-md" : ""}>
      <div className="flex items-center gap-1.5 text-muted-foreground">{icon}<span className="text-xs uppercase tracking-wide">{label}</span></div>
      <p className="font-medium mt-1">{value || "—"}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}

// ----------------------------------------------------------------
function Puppies({ litterId, puppies, prefUnit, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", collar_color: "", sex: "", color: "", markings: "", birth_order: puppies.length + 1, notes: "" });

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post(`/litters/${litterId}/puppies`, form);
      toast.success("Puppy added");
      setForm({ name: "", collar_color: "", sex: "", color: "", markings: "", birth_order: puppies.length + 2, notes: "" });
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add puppy");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Delete puppy "${label}"? Weights, ENS and socialization for this puppy will be removed.`)) return;
    try {
      const { data } = await api.delete(`/puppies/${id}`);
      const trashId = data?.trash_id;
      onChanged();
      toast.success(`${label} removed`, {
        duration: 10000,
        action: trashId
          ? {
              label: "Undo",
              onClick: async () => {
                try {
                  await api.post(`/puppies/_trash/${trashId}/restore`);
                  onChanged();
                  toast.success(`${label} restored`);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Could not restore");
                }
              },
            }
          : undefined,
      });
    } catch (err) {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="puppies-section">
      <div className="flex items-center justify-between mb-2">
        <SectionTitle overline="Litter" title={`Puppies (${puppies.length}/10)`} />
      </div>
      <p className="text-xs text-muted-foreground mb-4" data-testid="puppies-weights-hint">
        Add each puppy below to unlock <strong>daily weights</strong>, ENS grid rows, and per-puppy socialization tags. Weight tracking for the whole litter lives in the next section — one row per date, one column per puppy, all editable inline.
      </p>

      {puppies.length < 10 && (
        <Card className="border-dashed border-border shadow-none rounded-lg mb-4" data-testid="puppy-add-form">
          <CardContent className="p-5">
            <form onSubmit={add} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
              <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="puppy-form-name" /></Field>
              <Field label="Collar">
                <select value={form.collar_color} onChange={(e) => setForm({ ...form, collar_color: e.target.value })} className="h-9 w-full border border-border rounded-md bg-card px-2 text-sm" data-testid="puppy-form-collar">
                  <option value="">—</option>
                  {COLLAR_COLORS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Sex">
                <select value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })} className="h-9 w-full border border-border rounded-md bg-card px-2 text-sm" data-testid="puppy-form-sex">
                  <option value="">—</option><option value="M">Male</option><option value="F">Female</option>
                </select>
              </Field>
              <Field label="Birth order"><Input type="number" min="1" max="10" value={form.birth_order} onChange={(e) => setForm({ ...form, birth_order: parseInt(e.target.value, 10) || 0 })} data-testid="puppy-form-birth-order" /></Field>
              <Field label="Color"><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="black, sable…" data-testid="puppy-form-color" /></Field>
              <Field label="Markings"><Input value={form.markings} onChange={(e) => setForm({ ...form, markings: e.target.value })} placeholder="white blaze…" data-testid="puppy-form-markings" /></Field>
              <div className="col-span-full flex justify-end">
                <Button type="submit" disabled={adding} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="puppy-form-submit">
                  <Plus size={14} className="mr-1.5" /> {adding ? "Adding…" : "Add puppy"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="puppy-cards">
        {puppies.map((p) => <PuppyCard key={p.id} puppy={p} prefUnit={prefUnit} onChanged={onChanged} onDelete={() => remove(p.id, p.name || `#${p.birth_order}`)} />)}
      </div>
    </div>
  );
}

function PuppyCard({ puppy, prefUnit, onChanged, onDelete }) {
  const collar = collarFor(puppy.collar_color);
  const [weights, setWeights] = useState([]);
  const [w, setW] = useState({ weight: "", unit: suggestPuppyEntryUnit(prefUnit), entry_date: new Date().toISOString().slice(0, 10) });
  const [inviteOpen, setInviteOpen] = useState(false);

  const loadW = async () => {
    try {
      const { data } = await api.get(`/puppies/${puppy.id}/weights`);
      setWeights(data.items || []);
    } catch (err) {
      // Weight history is non-critical — log only, no toast (would be noisy
      // on a card that's loading lazily as the user scrolls).
      // eslint-disable-next-line no-console
      console.debug(`loadW failed for puppy ${puppy.id}`, err);
    }
  };
  useEffect(() => { loadW(); /* eslint-disable-next-line */ }, [puppy.id]);

  const addWeight = async (e) => {
    e.preventDefault();
    if (!w.weight || !w.entry_date) return;
    try {
      await api.post(`/puppies/${puppy.id}/weights`, { ...w, weight: parseFloat(w.weight) });
      setW({ ...w, weight: "" });
      loadW();
    } catch (err) {
      toast.error("Could not save weight");
    }
  };

  const latest = weights[weights.length - 1];

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid={`puppy-card-${puppy.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {collar && <span className={`inline-block h-4 w-4 rounded-full ${collar.swatch}`} title={collar.label} data-testid={`puppy-collar-${puppy.id}`} />}
            <div className="min-w-0">
              <p className="font-medium truncate" data-testid={`puppy-name-${puppy.id}`}>{puppy.name || `Puppy #${puppy.birth_order || "?"}`}</p>
              <p className="text-xs text-muted-foreground truncate">
                {sexLabel(puppy.sex)}
                {puppy.color ? ` · ${puppy.color}` : ""}
                {puppy.markings ? ` · ${puppy.markings}` : ""}
                {puppy.birth_order ? ` · #${puppy.birth_order}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => setInviteOpen(true)} className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-primary" data-testid={`puppy-invite-${puppy.id}`} aria-label="Invite new owner" title="Invite new owner">
              <Send size={14} />
            </button>
            <button onClick={onDelete} className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive" data-testid={`puppy-delete-${puppy.id}`} aria-label="Delete puppy">
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {latest && (
          <p className="text-xs text-muted-foreground mt-3" data-testid={`puppy-latest-weight-${puppy.id}`}>
            Latest weight: <strong className="text-foreground">{formatWeight(latest.weight, latest.unit, prefUnit)}</strong> on {latest.entry_date}
            {weights.length > 1 ? ` · ${weights.length} readings` : ""}
          </p>
        )}

        {puppy.notes && <p className="text-xs text-foreground/70 mt-3 whitespace-pre-wrap">{puppy.notes}</p>}
      </CardContent>
      <InviteOwnerDialog puppy={puppy} open={inviteOpen} onOpenChange={setInviteOpen} />
    </Card>
  );
}

// ----------------------------------------------------------------
function ENSGrid({ litterId, puppies, ens, bornOn, onReload }) {
  // ENS protocol runs days 3-16 of life. Compute the 14 calendar dates if we have a birth date.
  const days = useMemo(() => {
    if (!bornOn) return [];
    const start = new Date(bornOn + "T00:00:00");
    return Array.from({ length: 14 }, (_, i) => {
      const d = new Date(start); d.setDate(d.getDate() + 2 + i); // day 3..16
      return { day: 3 + i, iso: d.toISOString().slice(0, 10) };
    });
  }, [bornOn]);

  const lookup = (puppyId, iso) => ens.find((e) => e.puppy_id === puppyId && e.entry_date === iso) || {};
  const toggle = async (puppyId, iso, key, current) => {
    const existing = lookup(puppyId, iso);
    const patch = { ...existing, puppy_id: puppyId, entry_date: iso, [key]: !current };
    // Ensure all 5 booleans are present
    ENS_EXERCISES.forEach((ex) => { if (patch[ex.key] === undefined) patch[ex.key] = ex.key === key ? !current : false; });
    try {
      await api.post(`/litters/${litterId}/ens`, patch);
      onReload();
    } catch (err) {
      toast.error("Could not save ENS");
    }
  };

  if (!bornOn) {
    return (
      <Card className="border-dashed border-border shadow-none rounded-lg" data-testid="ens-empty">
        <CardContent className="p-6 text-sm text-muted-foreground">
          <SectionTitle overline="Days 3–16" title="ENS — Early Neurological Stimulation" />
          <p className="mt-2">
            Set a <strong>birth date</strong> above to unlock the 14-day ENS protocol grid. Each cell will let you tap through the five neurological stimulation exercises for every puppy.
          </p>
        </CardContent>
      </Card>
    );
  }
  if (puppies.length === 0) {
    return (
      <Card className="border-dashed border-border shadow-none rounded-lg" data-testid="ens-empty-nopuppies">
        <CardContent className="p-6 text-sm text-muted-foreground">
          <SectionTitle overline="Days 3–16" title="ENS — Early Neurological Stimulation" />
          <p className="mt-2">
            Add at least one puppy above and the ENS grid appears here — one row per puppy across days 3–16 of life.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div data-testid="ens-section">
      <SectionTitle overline="Days 3–16" title="ENS — Early Neurological Stimulation" />
      <div className="overflow-x-auto -mx-2 sm:mx-0">
        <table className="min-w-full text-xs border border-border rounded-md overflow-hidden" data-testid="ens-grid">
          <thead className="bg-secondary text-secondary-foreground">
            <tr>
              <th className="px-2 py-2 text-left sticky left-0 bg-secondary">Puppy</th>
              {days.map((d) => <th key={d.iso} className="px-2 py-2 whitespace-nowrap" title={d.iso}>D{d.day}</th>)}
            </tr>
          </thead>
          <tbody>
            {puppies.map((p) => {
              const collar = collarFor(p.collar_color);
              return (
                <tr key={p.id} className="border-t border-border" data-testid={`ens-row-${p.id}`}>
                  <td className="px-2 py-2 sticky left-0 bg-card">
                    <div className="flex items-center gap-2">
                      {collar && <span className={`inline-block h-3 w-3 rounded-full ${collar.swatch}`} />}
                      <span className="truncate max-w-[90px]">{p.name || `#${p.birth_order}`}</span>
                    </div>
                  </td>
                  {days.map((d) => {
                    const e = lookup(p.id, d.iso);
                    const doneCount = ENS_EXERCISES.filter((ex) => e[ex.key]).length;
                    return (
                      <td key={d.iso} className="px-2 py-1 text-center">
                        <button
                          className={`h-6 w-6 rounded text-[10px] font-bold border ${ensCellStyle(doneCount)}`}
                          onClick={() => {
                            // Cycle: open a quick toggle of all 5 exercises in sequence
                            // (simple UX: clicking the cell marks "all 5 done" or clears).
                            const allDone = doneCount === 5;
                            const patch = { puppy_id: p.id, entry_date: d.iso };
                            ENS_EXERCISES.forEach((ex) => { patch[ex.key] = !allDone; });
                            patch.notes = e.notes || "";
                            api.post(`/litters/${litterId}/ens`, patch).then(onReload).catch(() => toast.error("ENS save failed"));
                          }}
                          data-testid={`ens-cell-${p.id}-${d.iso}`}
                          title={`Day ${d.day} (${d.iso}) — ${doneCount}/5 exercises`}
                        >
                          {doneCount}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground mt-2">Tap a cell to mark all 5 ENS exercises complete for that puppy on that day. Tap again to clear.</p>
    </div>
  );
}

// ----------------------------------------------------------------
function Socialization({ litterId, puppies, entries, onReload }) {
  const [form, setForm] = useState({ category: "surfaces", activity: "", entry_date: new Date().toISOString().slice(0, 10), puppy_id: "", response: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.activity) return;
    setSaving(true);
    try {
      await api.post(`/litters/${litterId}/socialization`, { ...form, puppy_id: form.puppy_id || null });
      toast.success("Logged");
      setForm({ ...form, activity: "", response: "", notes: "" });
      onReload();
    } catch (err) {
      toast.error("Could not log");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/socialization/${id}`); onReload(); } catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid="socialization-section">
      <SectionTitle overline="Rule of 7s" title="Socialization log" />

      <Card className="border-dashed border-border shadow-none rounded-lg mb-4" data-testid="soc-add-form">
        <CardContent className="p-5">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Category">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-9 w-full border border-border rounded-md bg-card px-2 text-sm" data-testid="soc-category">
                {SOC_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Activity"><Input value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} placeholder="Vacuum / grass / new person…" required data-testid="soc-activity" /></Field>
            <Field label="Date"><Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} data-testid="soc-date" /></Field>
            <Field label="Puppy">
              <select value={form.puppy_id} onChange={(e) => setForm({ ...form, puppy_id: e.target.value })} className="h-9 w-full border border-border rounded-md bg-card px-2 text-sm" data-testid="soc-puppy">
                <option value="">Whole litter</option>
                {puppies.map((p) => <option key={p.id} value={p.id}>{p.name || `#${p.birth_order}`}</option>)}
              </select>
            </Field>
            <Field label="Response (optional)" full><Input value={form.response} onChange={(e) => setForm({ ...form, response: e.target.value })} placeholder="curious / startled then recovered…" data-testid="soc-response" /></Field>
            <Field label="Notes" full><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="soc-notes" /></Field>
            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="soc-submit">
                <Plus size={14} className="mr-1.5" /> {saving ? "Saving…" : "Log activity"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="soc-empty">No socialization entries yet.</p>
      ) : (
        <ul className="space-y-2" data-testid="soc-list">
          {entries.map((e) => (
            <li key={e.id} data-testid={`soc-entry-${e.id}`}>
              <Card className="border-border shadow-none rounded-lg">
                <CardContent className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{e.activity}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {e.entry_date} · <span className="capitalize">{SOC_CATEGORIES.find((c) => c.key === e.category)?.label || e.category}</span>
                      {e.puppy_id ? ` · ${puppies.find((p) => p.id === e.puppy_id)?.name || "Puppy"}` : " · Litter"}
                      {e.response ? ` · ${e.response}` : ""}
                    </p>
                    {e.notes && <p className="text-xs text-foreground/70 mt-1 whitespace-pre-wrap">{e.notes}</p>}
                  </div>
                  <button onClick={() => remove(e.id)} className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive" data-testid={`soc-delete-${e.id}`} aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ----------------------------------------------------------------
// LitterWeightsGrid — unified daily-weights table for the whole
// litter on a single page. Rows are dates, columns are puppies.
// Cells are inline-editable weight inputs so a breeder can weigh
// every puppy in the morning and enter the whole row in one place.
// ----------------------------------------------------------------
function LitterWeightsGrid({ puppies, prefUnit, bornOn }) {
  // Matrix of weight rows: [{ entry_date, byPuppy: { puppyId: {id, weight, unit} } }]
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addDate, setAddDate] = useState(new Date().toISOString().slice(0, 10));
  const [addUnit, setAddUnit] = useState(suggestPuppyEntryUnit(prefUnit));
  const [addValues, setAddValues] = useState({});   // puppyId -> string
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (puppies.length === 0) { setRows([]); return; }
    setLoading(true);
    try {
      const results = await Promise.all(
        puppies.map((p) =>
          api.get(`/puppies/${p.id}/weights`)
            .then((r) => ({ puppyId: p.id, items: r.data.items || [] }))
            .catch(() => ({ puppyId: p.id, items: [] })),
        ),
      );
      // Flatten into a matrix keyed by entry_date. Newest first — matches
      // how breeders read their notebook.
      const byDate = new Map();
      for (const { puppyId, items } of results) {
        for (const w of items) {
          const key = w.entry_date;
          if (!byDate.has(key)) byDate.set(key, { entry_date: key, byPuppy: {} });
          byDate.get(key).byPuppy[puppyId] = w;
        }
      }
      const list = [...byDate.values()].sort((a, b) => (b.entry_date || "").localeCompare(a.entry_date || ""));
      setRows(list);
    } finally { setLoading(false); }
  }, [puppies]);

  useEffect(() => { load(); }, [load]);

  // Day-of-life helper — shows "Day 3" next to the date when a birth date
  // is set. Useful context in the first two weeks.
  const dayOfLife = (iso) => {
    if (!bornOn || !iso) return "";
    try {
      const d = differenceInCalendarDays(parseISO(iso), parseISO(bornOn));
      if (d < 0) return "";
      return `Day ${d + 1}`;
    } catch { return ""; }
  };

  const submitRow = async (e) => {
    e.preventDefault();
    const entries = Object.entries(addValues)
      .filter(([, v]) => v !== "" && v != null && !isNaN(Number(v)));
    if (entries.length === 0) { toast.error("Enter at least one weight."); return; }
    setSaving(true);
    try {
      await Promise.all(
        entries.map(([puppyId, v]) =>
          api.post(`/puppies/${puppyId}/weights`, {
            weight: parseFloat(v),
            unit: addUnit,
            entry_date: addDate,
          }),
        ),
      );
      toast.success(`Logged ${entries.length} weight${entries.length === 1 ? "" : "s"}`);
      setAddValues({});
      load();
    } catch (err) {
      toast.error("Some weights didn't save. Try again.");
    } finally { setSaving(false); }
  };

  const removeReading = async (readingId) => {
    if (!readingId) return;
    if (!window.confirm("Delete this reading?")) return;
    try {
      await api.delete(`/puppy-weights/${readingId}`);
      load();
    } catch { toast.error("Could not delete"); }
  };

  if (puppies.length === 0) {
    return (
      <Card className="border-dashed border-border shadow-none rounded-lg" data-testid="litter-weights-empty">
        <CardContent className="p-6 text-sm text-muted-foreground">
          <SectionTitle overline="Litter" title="Daily weights" />
          <p className="mt-2">Add at least one puppy above and the daily-weights grid appears here.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div data-testid="litter-weights-section">
      <SectionTitle overline="Litter" title="Daily weights" />

      {/* Growth chart + per-puppy sparklines — a visual read of who's gaining
          before diving into the grid. */}
      <LitterGrowthChart puppies={puppies} rows={rows} prefUnit={prefUnit} bornOn={bornOn} />

      {/* Quick-add: one date, one weight per puppy, submit the whole row */}
      <Card className="border-dashed border-border shadow-none rounded-lg mb-4" data-testid="litter-weights-add-form">
        <CardContent className="p-4 sm:p-5">
          <form onSubmit={submitRow} className="space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Date</Label>
                <Input type="date" value={addDate} onChange={(e) => setAddDate(e.target.value)} className="mt-1 h-9" data-testid="litter-weights-add-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Unit</Label>
                <select value={addUnit} onChange={(e) => setAddUnit(e.target.value)} className="mt-1 h-9 border border-border rounded-md bg-card px-2 text-sm" data-testid="litter-weights-add-unit">
                  <option value="oz">oz</option><option value="lb">lb</option><option value="g">g</option><option value="kg">kg</option>
                </select>
              </div>
              <Button
                type="submit"
                size="sm"
                disabled={saving}
                className="bg-primary text-primary-foreground hover:bg-primary/90 h-9 ml-auto"
                data-testid="litter-weights-add-submit"
              >
                {saving ? "Saving…" : "Log row for all"}
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {puppies.map((p) => {
                const collar = collarFor(p.collar_color);
                return (
                  <div key={p.id} className="flex items-center gap-2 min-w-0" data-testid={`litter-weights-input-${p.id}`}>
                    {collar && <span className={`inline-block h-3 w-3 rounded-full shrink-0 ${collar.swatch}`} title={collar.label} />}
                    <span className="text-xs truncate flex-1" title={p.name || `#${p.birth_order}`}>{p.name || `#${p.birth_order || "?"}`}</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="—"
                      value={addValues[p.id] || ""}
                      onChange={(e) => setAddValues({ ...addValues, [p.id]: e.target.value })}
                      className="w-20 h-8 text-sm text-right"
                    />
                  </div>
                );
              })}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* History table — rows=date, cols=puppy */}
      {loading ? (
        <p className="text-sm text-muted-foreground py-4" data-testid="litter-weights-loading">Loading weights…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4" data-testid="litter-weights-empty-history">No weights logged yet. Use the row above to start.</p>
      ) : (
        <div className="overflow-x-auto -mx-2 sm:mx-0" data-testid="litter-weights-history">
          <table className="min-w-full text-xs border border-border rounded-md overflow-hidden">
            <thead className="bg-secondary text-secondary-foreground">
              <tr>
                <th className="px-2 py-2 text-left sticky left-0 bg-secondary whitespace-nowrap">Date</th>
                {puppies.map((p) => {
                  const collar = collarFor(p.collar_color);
                  return (
                    <th key={p.id} className="px-2 py-2 whitespace-nowrap text-left">
                      <div className="flex items-center gap-1.5">
                        {collar && <span className={`inline-block h-2.5 w-2.5 rounded-full ${collar.swatch}`} />}
                        <span className="truncate max-w-[80px]">{p.name || `#${p.birth_order}`}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.entry_date} className="border-t border-border" data-testid={`litter-weights-row-${row.entry_date}`}>
                  <td className="px-2 py-2 sticky left-0 bg-card whitespace-nowrap">
                    <div className="font-medium">{row.entry_date}</div>
                    {dayOfLife(row.entry_date) && (
                      <div className="text-[10px] text-muted-foreground">{dayOfLife(row.entry_date)}</div>
                    )}
                  </td>
                  {puppies.map((p) => {
                    const w = row.byPuppy[p.id];
                    return (
                      <td key={p.id} className="px-2 py-2 whitespace-nowrap" data-testid={`litter-weights-cell-${p.id}-${row.entry_date}`}>
                        {w ? (
                          <div className="flex items-center gap-1">
                            <span className="tabular-nums">{formatWeight(w.weight, w.unit, prefUnit)}</span>
                            <button
                              type="button"
                              onClick={() => removeReading(w.id)}
                              className="text-muted-foreground hover:text-destructive p-0.5"
                              aria-label={`Delete reading for ${p.name || `#${p.birth_order}`} on ${row.entry_date}`}
                            >
                              <Trash2 size={10} />
                            </button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// LitterGrowthChart — multi-line growth chart of every puppy on the
// same axis, plus a strip of per-puppy sparklines with latest weight
// and gain/loss delta. All weights normalized to the user's preferred
// display unit (grams internally, converted at render time) so a
// switch of unit mid-litter doesn't create discontinuities.
// ----------------------------------------------------------------
const SPARK_PALETTE = ["#3b82f6", "#22c55e", "#f59e0b", "#a855f7", "#ec4899", "#ef4444", "#14b8a6", "#eab308", "#0ea5e9", "#f97316"];

function LitterGrowthChart({ puppies, rows, prefUnit, bornOn }) {
  // Build a per-date, per-puppy series in the user's preferred display unit.
  // The chart x-axis is `dayKey` — either day-of-life (when bornOn is set)
  // or an ordinal position across sorted dates.
  const { chartData, sortedDates, perPuppy } = useMemo(() => {
    const displayUnit = prefUnit === "kg" ? "g" : "oz";
    const toDisplay = (grams) => (displayUnit === "g" ? grams : grams / 28.3495);

    // Sort rows ascending for the chart. Existing `rows` is newest-first.
    const asc = [...rows].sort((a, b) => (a.entry_date || "").localeCompare(b.entry_date || ""));

    // Per-puppy series (used both for the multi-line chart and the sparklines).
    const perPuppyMap = new Map();
    for (const p of puppies) perPuppyMap.set(p.id, []);
    for (const row of asc) {
      for (const p of puppies) {
        const w = row.byPuppy[p.id];
        if (!w) continue;
        perPuppyMap.get(p.id).push({
          date: row.entry_date,
          value: toDisplay(toGrams(w.weight, w.unit)),
        });
      }
    }

    // Multi-line data: one object per unique date, with a numeric field per puppy.
    const data = asc.map((r) => {
      const point = { date: r.entry_date };
      if (bornOn) {
        try { point.day = differenceInCalendarDays(parseISO(r.entry_date), parseISO(bornOn)) + 1; } catch { /* ignore */ }
      }
      for (const p of puppies) {
        const w = r.byPuppy[p.id];
        point[p.id] = w ? toDisplay(toGrams(w.weight, w.unit)) : null;
      }
      return point;
    });

    return { chartData: data, sortedDates: asc.map((r) => r.entry_date), perPuppy: perPuppyMap };
  }, [puppies, rows, prefUnit, bornOn]);

  // Fading puppies — a classic red-flag pattern is two consecutive daily
  // losses in the first two weeks (dam/latch issues, aspiration, colostrum
  // troubles). Detect ≥3 readings where the last two deltas are negative.
  const fading = useMemo(() => {
    const out = [];
    for (const p of puppies) {
      const s = perPuppy.get(p.id) || [];
      if (s.length < 3) continue;
      const a = s[s.length - 3], b = s[s.length - 2], c = s[s.length - 1];
      // Small threshold (0.05 of display unit) to avoid flagging noise.
      const eps = 0.05;
      if (b.value < a.value - eps && c.value < b.value - eps) {
        out.push({ puppy: p, series: s });
      }
    }
    return out;
  }, [puppies, perPuppy]);

  const displayUnitLabel = prefUnit === "kg" ? "g" : "oz";

  // CSV export — one row per date, one column per puppy, values in the
  // display unit already used on the charts. Handy for a vet visit.
  const downloadCSV = () => {
    const displayUnit = prefUnit === "kg" ? "g" : "oz";
    const dp = displayUnit === "g" ? 0 : 1;
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = ["Date"];
    if (bornOn) headers.push("Day of life");
    for (const p of puppies) headers.push(p.name || `#${p.birth_order || "?"}`);
    headers.push(`Unit`);

    // Build ascending rows (chartData is already ascending).
    const lines = [headers.map(escape).join(",")];
    for (const r of chartData) {
      const row = [r.date];
      if (bornOn) row.push(r.day != null ? `Day ${r.day}` : "");
      for (const p of puppies) {
        const v = r[p.id];
        row.push(v == null ? "" : v.toFixed(dp));
      }
      row.push(displayUnit);
      lines.push(row.map(escape).join(","));
    }
    const filename = `weights-${(puppies[0]?.name || "litter").toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`;
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const colorFor = (puppy, idx) => {
    const c = collarFor(puppy.collar_color);
    // Fall back to a stable palette entry when the puppy has no collar.
    return c?.hex || SPARK_PALETTE[idx % SPARK_PALETTE.length];
  };

  // Nothing to show yet.
  if (rows.length === 0 || puppies.length === 0) return null;

  return (
    <div className="space-y-4 mb-4" data-testid="litter-growth-block">
      {fading.length > 0 ? (
        <div
          className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 sm:p-4 flex items-start gap-3"
          role="alert"
          data-testid="fading-callout"
        >
          <div className="h-8 w-8 rounded-md bg-rose-500/15 text-rose-700 flex items-center justify-center shrink-0">
            <TrendingDown size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-rose-700">
              {fading.length === 1 ? "One puppy has lost weight two days in a row" : `${fading.length} puppies have lost weight two days in a row`}
            </p>
            <p className="text-xs text-foreground/85 mt-0.5 leading-relaxed">
              {fading.map((f) => f.puppy.name || `#${f.puppy.birth_order || "?"}`).join(", ")}
              {" "}— worth a check for latch issues, dehydration, or aspiration. Call your vet if the trend continues.
            </p>
          </div>
        </div>
      ) : null}

      {/* Overlay chart — everyone on one axis */}
      <Card className="border-border shadow-none rounded-lg" data-testid="litter-growth-chart">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
              Growth · litter overlay ({displayUnitLabel})
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex flex-wrap gap-x-3 gap-y-1" data-testid="litter-growth-legend">
                {puppies.map((p, i) => (
                  <span key={p.id} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={`litter-growth-legend-${p.id}`}>
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colorFor(p, i) }} aria-hidden />
                    <span className="truncate max-w-[100px]">{p.name || `#${p.birth_order || i + 1}`}</span>
                  </span>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={downloadCSV}
                className="h-7 text-[11px]"
                data-testid="litter-weights-export"
                title="Download the full weight matrix as CSV"
              >
                <Download size={12} className="mr-1" /> CSV
              </Button>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 6, right: 12, left: 0, bottom: 4 }}>
                <YAxis
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                  width={38}
                  label={{ value: displayUnitLabel, angle: -90, position: "insideLeft", offset: 8, style: { fontSize: 10, fill: "hsl(var(--muted-foreground))" } }}
                />
                <RTooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid hsl(var(--border))" }}
                  labelFormatter={(_l, payload) => {
                    const p = payload?.[0]?.payload;
                    if (!p) return "";
                    return p.day ? `${p.date} · Day ${p.day}` : p.date;
                  }}
                  formatter={(v, key) => {
                    const p = puppies.find((pp) => pp.id === key);
                    const rounded = displayUnitLabel === "g" ? Math.round(v) : v.toFixed(1);
                    return [`${rounded} ${displayUnitLabel}`, p?.name || `#${p?.birth_order || key}`];
                  }}
                />
                {puppies.map((p, i) => (
                  <Line
                    key={p.id}
                    type="monotone"
                    dataKey={p.id}
                    stroke={colorFor(p, i)}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">
            All weights converted to {displayUnitLabel === "g" ? "grams" : "ounces"} for consistency. Change your unit preference in Settings to switch to lb / kg.
          </p>
        </CardContent>
      </Card>

      {/* Per-puppy sparklines — one card each, latest weight + delta from prior */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="puppy-sparklines">
        {puppies.map((p, i) => (
          <PuppyGrowthSparkline
            key={p.id}
            puppy={p}
            series={perPuppy.get(p.id) || []}
            color={colorFor(p, i)}
            prefUnit={prefUnit}
            isFading={fading.some((f) => f.puppy.id === p.id)}
          />
        ))}
      </div>
    </div>
  );
}

function PuppyGrowthSparkline({ puppy, series, color, prefUnit, isFading }) {
  const latest = series[series.length - 1];
  const prev = series[series.length - 2];
  const delta = latest && prev ? latest.value - prev.value : null;
  const trend = delta == null ? null : delta > 0.05 ? "up" : delta < -0.05 ? "down" : "flat";
  const collar = collarFor(puppy.collar_color);

  // Display value using formatWeight helper — it handles the unit choice.
  // Since our series is already in the display unit's native number, we can
  // build a synthetic {weight, unit} for the helper by round-tripping.
  const displayUnit = prefUnit === "kg" ? "g" : "oz";
  const latestLabel = latest != null ? formatWeight(latest.value, displayUnit, prefUnit) : "—";
  const deltaLabel = delta == null ? null : `${delta > 0 ? "+" : ""}${displayUnit === "g" ? Math.round(delta) : delta.toFixed(1)} ${displayUnit}`;

  const trendClass = trend === "up" ? "text-emerald-600" : trend === "down" ? "text-rose-600" : "text-muted-foreground";
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const cardCls = isFading
    ? "rounded-md border-2 border-rose-500/70 bg-rose-500/5 p-2.5 flex flex-col gap-1 min-w-0"
    : "rounded-md border border-border bg-card p-2.5 flex flex-col gap-1 min-w-0";
  const strokeColor = isFading ? "#e11d48" : color;

  return (
    <div
      className={cardCls}
      data-testid={`puppy-spark-${puppy.id}`}
      data-fading={isFading ? "true" : "false"}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        {collar && <span className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ${collar.swatch}`} title={collar.label} />}
        <span className="text-xs font-medium truncate">{puppy.name || `#${puppy.birth_order || "?"}`}</span>
        {isFading ? (
          <span
            className="ml-auto inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-rose-500/15 text-rose-700 border border-rose-500/30 text-[9px] uppercase tracking-wider font-bold shrink-0"
            data-testid={`puppy-spark-fading-${puppy.id}`}
            title="Two consecutive daily losses — worth a check"
          >
            Fading
          </span>
        ) : null}
      </div>
      <div className="flex items-baseline justify-between gap-2 min-w-0">
        <span className="text-lg font-display font-bold tabular-nums leading-none">{latestLabel}</span>
        {trend ? (
          <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${trendClass}`} data-testid={`puppy-spark-delta-${puppy.id}`}>
            <TrendIcon size={11} /> {deltaLabel}
          </span>
        ) : null}
      </div>
      <div className="h-8">
        {series.length >= 2 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={strokeColor}
                strokeWidth={1.75}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center">
            <span className="text-[10px] text-muted-foreground italic">
              {series.length === 1 ? "1 reading" : "no data"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

